import type { LineCanvas } from "@/types/canvas.types";
import type { LineDetail } from "@/types/line-editor.types";
import type { MachineDetail } from "@/types/lines.types";
import type { ValidationIssue } from "@/types/line-editor.types";
import { formatIssue } from "./issue-messages";

const CODE_REGEX = /^[A-Za-z0-9_-]+$/;

export function computeEditIssues(input: {
  line: LineDetail;
  machines: MachineDetail[];
  canvas: LineCanvas;
}): ValidationIssue[] {
  const { line, machines, canvas } = input;
  const issues: ValidationIssue[] = [];

  if (!line.name?.trim()) {
    const f = formatIssue("header:name-required");
    issues.push({
      id: "header:name-required",
      message: f.message,
      detail: f.detail,
      group: "header",
      severity: "danger",
      target: { kind: "header", field: "name" },
    });
  }

  if (machines.length === 0) {
    const f = formatIssue("machines:empty");
    issues.push({
      id: "machines:empty",
      message: f.message,
      detail: f.detail,
      group: "machines",
      severity: "danger",
    });
  }
  for (const m of machines) {
    const ctx = { machineName: m.name, machineCode: m.code };
    if (!m.code?.trim()) {
      const f = formatIssue("machine:code-empty", ctx);
      issues.push({
        id: `machine:${m.id}:code-empty`,
        message: f.message,
        detail: f.detail,
        group: "machines",
        severity: "danger",
        target: { kind: "machine", id: m.id, field: "code" },
      });
    } else if (!CODE_REGEX.test(m.code)) {
      const f = formatIssue("machine:code-invalid", ctx);
      issues.push({
        id: `machine:${m.id}:code-invalid`,
        message: f.message,
        detail: f.detail,
        group: "machines",
        severity: "danger",
        target: { kind: "machine", id: m.id, field: "code" },
      });
    }
  }

  const usedMachineIds = new Set(
    canvas.nodes
      .filter((n) => n.type === "MACHINE" && typeof n.data?.machineId === "string")
      .map((n) => n.data.machineId as string)
  );
  for (const m of machines) {
    if (!usedMachineIds.has(m.id)) {
      const f = formatIssue("machine:orphan", { machineName: m.name });
      issues.push({
        id: `machine:${m.id}:orphan`,
        message: f.message,
        detail: f.detail,
        group: "canvas",
        severity: "warning",
        target: { kind: "machine", id: m.id },
      });
    }
  }

  const edgesByNode = new Map<string, number>();
  for (const e of canvas.edges) {
    edgesByNode.set(e.sourceNodeId, (edgesByNode.get(e.sourceNodeId) ?? 0) + 1);
    edgesByNode.set(e.targetNodeId, (edgesByNode.get(e.targetNodeId) ?? 0) + 1);
  }
  for (const n of canvas.nodes) {
    if (n.type !== "MACHINE") continue;
    if ((edgesByNode.get(n.id) ?? 0) === 0) {
      const f = formatIssue("canvas:node:no-link", { nodeLabel: n.label });
      issues.push({
        id: `canvas:node:${n.id}:no-link`,
        message: f.message,
        detail: f.detail,
        group: "canvas",
        severity: "warning",
        target: { kind: "node", id: n.id },
      });
    }
  }

  const startNode = canvas.nodes.find((n) => n.type === "START");
  const endNode = canvas.nodes.find((n) => n.type === "END");
  if (startNode && (edgesByNode.get(startNode.id) ?? 0) === 0) {
    const f = formatIssue("canvas:start-disconnected");
    issues.push({
      id: "canvas:start-disconnected",
      message: f.message,
      detail: f.detail,
      group: "canvas",
      severity: "danger",
      target: { kind: "node", id: startNode.id },
    });
  }
  if (endNode && (edgesByNode.get(endNode.id) ?? 0) === 0) {
    const f = formatIssue("canvas:end-disconnected");
    issues.push({
      id: "canvas:end-disconnected",
      message: f.message,
      detail: f.detail,
      group: "canvas",
      severity: "danger",
      target: { kind: "node", id: endNode.id },
    });
  }

  return issues;
}
